import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";

import "./Timer.scss";
import ControlButton from "../ControlButton/ControlButton";
import ActionButton from "../ActionButton/ActionButton";
import { setTimer, setIsReadPressed } from "../../../../store";
import { SimManager } from "@learningblocks/simulation-engine";

interface TimerInterface {
  hours: number;
  minutes: number;
  seconds: number;
}
interface TimerProps {
  title: string;
  timer: TimerInterface;
  handleReaderErrors: () => boolean;
}

const Timer = ({ title, timer, handleReaderErrors }: TimerProps) => {
  const dispatch = useDispatch();
  const [isRunning, setIsRunning] = useState(false);

  useEffect(() => {
    let interval: number;

    if (isRunning) {
      interval = window.setInterval(() => {
        let updatedTimer = { ...timer };
        // If timer is already at 00:00:00, stop
        if (
          updatedTimer.hours === 0 &&
          updatedTimer.minutes === 0 &&
          updatedTimer.seconds === 0
        ) {
          setIsRunning(false);
          return;
        }
        if (updatedTimer.seconds > 0) {
          updatedTimer.seconds -= 1;
        } else {
          if (updatedTimer.minutes > 0) {
            updatedTimer.minutes -= 1;
            updatedTimer.seconds = 59;
          } else if (updatedTimer.hours > 0) {
            updatedTimer.hours -= 1;
            updatedTimer.minutes = 59;
            updatedTimer.seconds = 59;
          }
        }
        dispatch(setTimer(updatedTimer));
      }, 33);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isRunning, timer, dispatch]);

  const handleStart = () => {
    if (handleReaderErrors()) {
      return;
    }

    dispatch(setIsReadPressed(true));
    setIsRunning(true);

    SimManager.getInstance().openModal("AnimationModal", "", {
      animation: "enzyme_activity_flow",
    });
  };

  const handleStop = () => {
    setIsRunning(false);
  };

  const incrementHours = () => {
    if (!isRunning) {
      const updatedTimer = { ...timer, hours: (timer.hours + 1) % 100 };
      dispatch(setTimer(updatedTimer));
    }
  };

  const decrementHours = () => {
    if (!isRunning) {
      const updatedTimer = {
        ...timer,
        hours: timer.hours > 0 ? timer.hours - 1 : 99,
      };
      dispatch(setTimer(updatedTimer));
    }
  };

  const incrementMinutes = () => {
    if (!isRunning) {
      const updatedTimer = { ...timer, minutes: (timer.minutes + 1) % 60 };
      dispatch(setTimer(updatedTimer));
    }
  };

  const decrementMinutes = () => {
    if (!isRunning) {
      const updatedTimer = {
        ...timer,
        minutes: timer.minutes > 0 ? timer.minutes - 1 : 59,
      };
      dispatch(setTimer(updatedTimer));
    }
  };

  const incrementSeconds = () => {
    if (!isRunning) {
      const updatedTimer = { ...timer, seconds: (timer.seconds + 1) % 60 };
      dispatch(setTimer(updatedTimer));
    }
  };

  const decrementSeconds = () => {
    if (!isRunning) {
      const updatedTimer = {
        ...timer,
        seconds: timer.seconds > 0 ? timer.seconds - 1 : 59,
      };
      dispatch(setTimer(updatedTimer));
    }
  };

  const formatTime = (time: number) => {
    return time.toString().padStart(2, "0");
  };

  return (
    <div className="timer">
      <h2 className="timer-title">{title}</h2>
      <div className="timer-display" aria-label="Timer Display">
        <div className="time-segments">
          <div className="time-segment" aria-label="Hours">
            <div className="segment-label">HH</div>
            <div className="time-value">{formatTime(timer.hours)}</div>
          </div>
          <div className="separator">:</div>
          <div className="time-segment" aria-label="Minutes">
            <div className="segment-label">MM</div>
            <div className="time-value">{formatTime(timer.minutes)}</div>
          </div>
          <div className="separator">:</div>
          <div className="time-segment" aria-label="Seconds">
            <div className="segment-label">SS</div>
            <div className="time-value">{formatTime(timer.seconds)}</div>
          </div>
        </div>
      </div>

      <div className="timer-controls">
        <div className="control-column hours-column">
          <ControlButton direction="up" onClick={incrementHours} />
          <div className="control-label">HH</div>
          <ControlButton direction="down" onClick={decrementHours} />
        </div>
        <div className="control-column minutes-column">
          <ControlButton direction="up" onClick={incrementMinutes} />
          <div className="control-label">MM</div>
          <ControlButton direction="down" onClick={decrementMinutes} />
        </div>
        <div className="control-column seconds-column">
          <ControlButton direction="up" onClick={incrementSeconds} />
          <div className="control-label">SS</div>
          <ControlButton direction="down" onClick={decrementSeconds} />
        </div>
      </div>

      <div className="action-buttons">
        <ActionButton type="stop" label="Stop" onClick={handleStop} />
        <ActionButton
          type="read"
          label="Read"
          disabled={isRunning}
          onClick={!isRunning ? handleStart : () => {}}
        />
      </div>
    </div>
  );
};

export default Timer;
